import React from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import { Users, Check, LogOut as LogOutIcon, X as XIcon } from "lucide-react";

/**
 * Renders a list of visitors — either "currently inside" or the full log.
 * Also wires host Accept/Decline and Check-out actions.
 */
export default function VisitorList({ rows, onCheckOut, showCheckout, currentUserId }) {
  if (rows.length === 0) {
    return <div className="text-sm text-[#4A5A50] py-8 text-center">Empty.</div>;
  }
  const respond = async (v, action) => {
    try {
      await api.post(`/visitors/${v.id}/host-response`, { action });
      toast.success(
        action === "accept" ? "Accepted — watchman notified" : "Declined — watchman notified",
      );
      onCheckOut && onCheckOut();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };
  const checkout = async (v) => {
    try {
      await api.post(`/visitors/${v.id}/check-out`);
      toast.success("Checked out");
      onCheckOut && onCheckOut();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <ul className="divide-y divide-[#E4DFCF] bg-white rounded-xl border border-[#E4DFCF]">
      {rows.map((v) => (
        <li
          key={v.id}
          className="p-4 flex items-start justify-between gap-3"
          data-testid={`visitor-row-${v.id}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            {v.photo_url ? (
              <img src={fileUrl(v.photo_url)} alt="" className="w-12 h-12 rounded-full object-cover"/>
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#F1EDDF] flex items-center justify-center">
                <Users size={18} className="text-[#0F5E39]"/>
              </div>
            )}
            <div className="min-w-0">
              <div className="font-medium truncate">{v.name}</div>
              <div className="text-xs text-[#4A5A50] truncate">
                {v.company ? `${v.company} · ` : ""}Host: {v.host_name || "—"} · {v.purpose || "visit"}
              </div>
              <div className="text-[10px] text-[#4A5A50]">
                In: {v.check_in_at ? format(new Date(v.check_in_at), "dd MMM HH:mm") : "—"}
                {v.check_out_at ? ` · Out: ${format(new Date(v.check_out_at), "dd MMM HH:mm")}` : ""}
              </div>
              {v.host_response && (
                <div className={`text-[10px] uppercase tracking-widest mt-1 inline-block px-2 py-0.5 rounded-full ${
                  v.host_response === "accept" ? "bg-[#E1F0E6] text-[#0F5E39]" : "bg-red-50 text-red-700"
                }`}>
                  Host {v.host_response}ed
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Host Accept/Decline: only when I am the host, no response yet, still inside */}
            {currentUserId && v.host_user_id === currentUserId && !v.host_response && !v.check_out_at && (
              <>
                <Button
                  size="sm"
                  className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-8 px-3"
                  onClick={() => respond(v, "accept")}
                  data-testid={`visitor-accept-${v.id}`}
                >
                  <Check size={12} className="mr-1"/> Accept
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full border-red-300 text-red-700 hover:bg-red-50 h-8 px-3"
                  onClick={() => respond(v, "decline")}
                  data-testid={`visitor-decline-${v.id}`}
                >
                  <XIcon size={12} className="mr-1"/> Decline
                </Button>
              </>
            )}
            {showCheckout && !v.check_out_at && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => checkout(v)}
                data-testid={`visitor-checkout-${v.id}`}
              >
                <LogOutIcon size={12} className="mr-1"/> Check out
              </Button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
